import { HttpErrorResponse } from '@angular/common/http';

/** How many class-validator messages to show before collapsing the rest. */
const MAX_MESSAGES = 3;

/**
 * The message to show the user for a failed API call, as `Error (<code>): <message>`.
 *
 * The code is the BE's domain `errorCode` when the body has one, else the HTTP
 * status (`network` when the request never got a response) — so a user reporting
 * the toast gives us something to search for.
 *
 * The message is the BE body's `message` — a string, or a string[] when
 * class-validator rejects the DTO — and never `HttpErrorResponse.message`, which
 * is Angular's own "Http failure response for <url>: 400 OK" and means nothing to
 * a user. Falls back to `fallback` when the body carries no message (network
 * failure, HTML error page, empty body).
 *
 * Non-HTTP errors (e.g. a failed cloud-storage upload) have no code and are
 * returned bare.
 */
export function apiErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof HttpErrorResponse) {
    return `Error (${errorCode(error)}): ${bodyMessage(error.error) ?? fallback}`;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

function errorCode(error: HttpErrorResponse): string {
  const code = error.error?.errorCode;
  if (typeof code === 'string' && code) return code;
  return error.status ? String(error.status) : 'network';
}

function bodyMessage(body: any): string | null {
  const raw = body?.message;
  if (Array.isArray(raw)) {
    const messages = raw.filter((m): m is string => typeof m === 'string' && !!m.trim());
    if (!messages.length) return null;
    const shown = messages.slice(0, MAX_MESSAGES).join('; ');
    const hidden = messages.length - MAX_MESSAGES;
    return hidden > 0 ? `${shown} (+${hidden} more)` : shown;
  }
  return typeof raw === 'string' && raw.trim() ? raw : null;
}
